import React, { Fragment, useState } from 'react';

import classNames from 'classnames';

import { canRaiseDc, canRaiseEa, canRaisePr, priorityPillClass, statusDotClass, statusPillClass, tableMoney } from './planHelpers';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * Column set for the register. The dept-scoped Plan (IB-702) shows explicit Description + Value
 * columns (`showDescriptionValue`), whereas the central Plan (IB-700) keeps them inline in the
 * Procurement name cell. Computed per-render so the group-header `colSpan` always matches.
 */
const columnsFor = (showDescriptionValue) =>
    showDescriptionValue
        ? [
              $t('Procurement'),
              $t('Description'),
              $t('Value'),
              $t('Departments'),
              $t('Owner'),
              $t('Policy'),
              $t('Priority'),
              $t('Complexity'),
              $t('Status'),
              $t('Actions'),
          ]
        : [$t('Procurement'), $t('Departments'), $t('Owner'), $t('Policy'), $t('Priority'), $t('Complexity'), $t('Status'), $t('Actions')];

/** A single greyed-not-hidden "+ TYPE" raise button. */
const RaiseButton = ({ label, enabled, onClick }) => (
    <button
        type="button"
        disabled={!enabled}
        onClick={enabled ? onClick : undefined}
        className={classNames(
            'tw-inline-flex tw-items-center tw-rounded-md tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-whitespace-nowrap tw-transition-colors',
            {
                'tw-border-blue-25 tw-text-blue hover:tw-bg-blue-10': enabled,
                'tw-border-grey tw-text-charcoal-25 tw-cursor-not-allowed': !enabled,
            },
        )}
    >
        {label}
    </button>
);

const DepartmentChips = ({ names }) => {
    if (!names || !names.length) {
        return <span className="tw-text-charcoal-50">{$t('No department')}</span>;
    }

    return (
        <div className="tw-flex tw-flex-wrap tw-gap-1">
            {names.map((name) => (
                <span key={name} className="tw-inline-flex tw-rounded tw-bg-charcoal-10 tw-px-2 tw-py-0.5 tw-text-xs tw-text-charcoal-75">
                    {name}
                </span>
            ))}
        </div>
    );
};

const RegisterRow = ({ item, currencySymbol, completed, settings, showDescriptionValue, onEdit, onView, onDelete, onCreatePR, onCreateEA, onCreateDC }) => {
    const statusTitle = item.status?.title || 'Not Started';

    const prEnabled = !completed && settings.isPrEnabled && canRaisePr(statusTitle);
    // An EA (purchase order) may only be raised once the item's PR is APPROVED — matching the
    // legacy flow. The sourcing-status check alone is not enough (a "PR Submitted" item may still
    // be awaiting sign-off), so require pr_approved too.
    const eaEnabled = !completed && settings.isEaEnabled && canRaiseEa(statusTitle) && item.pr_approved;
    const dcEnabled = !completed && settings.isDirectCommitEnabled && canRaiseDc(statusTitle);

    return (
        <tr className="tw-border-t tw-border-grey hover:tw-bg-charcoal-10">
            {showDescriptionValue ? (
                <Fragment>
                    <td className="tw-px-3 tw-py-3 tw-align-top">
                        <div className="tw-font-medium tw-text-charcoal">{item.name}</div>
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">
                        <span className="tw-line-clamp-2">{item.description || '—'}</span>
                    </td>
                    <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{tableMoney(currencySymbol, item.value)}</td>
                </Fragment>
            ) : (
                <td className="tw-px-3 tw-py-3 tw-align-top">
                    <div className="tw-font-medium tw-text-charcoal">{item.name}</div>
                    <div className="tw-text-sm tw-text-charcoal-50">{tableMoney(currencySymbol, item.value)}</div>
                    {item.description && <div className="tw-mt-0.5 tw-text-xs tw-text-charcoal-50 tw-line-clamp-2">{item.description}</div>}
                </td>
            )}
            <td className="tw-px-3 tw-py-3 tw-align-top">
                <DepartmentChips names={item.departments_names} />
            </td>
            <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{item.owner_name || '—'}</td>
            <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{item.policy?.method || '—'}</td>
            <td className="tw-px-3 tw-py-3 tw-align-top">
                {item.priority_label && (
                    <span
                        className={classNames(
                            'tw-inline-flex tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold',
                            priorityPillClass(item.priority_label),
                        )}
                    >
                        {item.priority_label}
                    </span>
                )}
            </td>
            <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{item.complexity_label || '—'}</td>
            <td className="tw-px-3 tw-py-3 tw-align-top">
                <span
                    className={classNames(
                        'tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold',
                        statusPillClass(statusTitle),
                    )}
                >
                    <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', statusDotClass(statusTitle))} />
                    {statusTitle}
                </span>
            </td>
            <td className="tw-px-3 tw-py-3 tw-align-top">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        type="button"
                        onClick={() => onEdit(item)}
                        className="tw-text-blue hover:tw-text-dark-blue"
                        aria-label={$t('Edit')}
                        title={$t('Edit')}
                    >
                        <EditIcon className="tw-w-3.5 tw-h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onView(item)}
                        className="tw-text-charcoal-50 hover:tw-text-charcoal"
                        aria-label={$t('View')}
                        title={$t('View')}
                    >
                        <EyeIcon className="tw-w-3.5 tw-h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(item)}
                        className="tw-text-red hover:tw-text-red-75"
                        aria-label={$t('Delete')}
                        title={$t('Delete')}
                    >
                        <TrashIcon className="tw-w-3.5 tw-h-3.5" />
                    </button>
                    <span className="tw-mx-1 tw-h-4 tw-w-px tw-bg-grey" />
                    <RaiseButton label={$t('+ PR')} enabled={prEnabled} onClick={() => onCreatePR(item)} />
                    <RaiseButton label={$t('+ EA')} enabled={eaEnabled} onClick={() => onCreateEA(item)} />
                    <RaiseButton label={$t('+ DC')} enabled={dcEnabled} onClick={() => onCreateDC(item)} />
                </div>
            </td>
        </tr>
    );
};

const RegisterGroup = ({ group, groupKey, columnCount, ...rowProps }) => {
    const [open, setOpen] = useState(true);
    const heading = group[groupKey];

    return (
        <Fragment>
            <tr className="tw-bg-charcoal-10">
                <td colSpan={columnCount} className="tw-px-3 tw-py-2">
                    <button
                        type="button"
                        onClick={() => setOpen((prev) => !prev)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-charcoal"
                    >
                        {open ? <ChevronUpIcon className="tw-w-2 tw-h-2" /> : <ChevronDownIcon className="tw-w-2 tw-h-2" />}
                        {heading}
                    </button>
                </td>
            </tr>
            {open && (group.items ?? []).map((item) => <RegisterRow key={item.id} item={item} {...rowProps} />)}
        </Fragment>
    );
};

/**
 * IB-700 — one register table (Outstanding or Completed), grouped by the active grouping
 * (category or department). Renders an explicit empty state rather than disappearing.
 */
const RegisterTable = ({ title, groups, groupKey, loading, emptyText, pagination, showDescriptionValue = false, ...rowProps }) => {
    const columns = columnsFor(showDescriptionValue);

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
            <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <h4 className="tw-text-base tw-font-semibold tw-text-blue">{title}</h4>
                {pagination && (
                    <div className="tw-flex tw-items-center tw-gap-4">
                        <PaginationInfo from={pagination.from} to={pagination.to} total={pagination.total} />
                        {pagination.lastPage > 1 && (
                            <TablePagination
                                lastPage={pagination.lastPage}
                                currentPage={pagination.page}
                                pageChange={(selection) => pagination.setPage(+selection.selected + 1)}
                            />
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : !groups || !groups.length ? (
                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-charcoal-50">{emptyText}</p>
            ) : (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-left tw-min-w-[64rem]">
                        <thead>
                            <tr className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                {columns.map((column) => (
                                    <th key={column} className="tw-px-3 tw-pb-2">
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((group) => (
                                <RegisterGroup
                                    key={group[groupKey]}
                                    group={group}
                                    groupKey={groupKey}
                                    columnCount={columns.length}
                                    showDescriptionValue={showDescriptionValue}
                                    {...rowProps}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RegisterTable;
